// import { io } from 'socket.io-client';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { ROUTE_PATHS, SOCKET_URL } from '@/constants';
import { createSocketClient } from '@/realtime/createSocketClient';

// import useKanbanBoard, { setSocketId } from '../kanbanBoard';

import type { Socket } from 'socket.io-client';
import { queryClient } from '@/queryClient';
import {
  useCheatSheetStore,
  useDailyRosterStore,
  useTransportRosterStore
} from '..';
import {
  addLessonToDate,
  appendLessonToInstructor,
  removeLessonById,
  removeLessonByScheduleGroupId,
  toYyyyMmDd
} from '@/utils/common';
import {
  DailyRosterInstructor,
  LessonSchedule
} from '@/constants/Interfaces/CommonInterfaces';
import useInstructorRosterStore from '../instructorRosterStore';
import { getMonth } from 'date-fns';

// Minimal shared shapes for roster updates
type ScheduleGroup = {
  id: number;
};

type LessonItem = {
  id?: number | string;
  start?: string;
  instructor?: { id?: number } | null;
  car?: { id?: number } | null;
  schedule_group?: ScheduleGroup;
  schedule_group_id?: number | string; // Flattened for internal use
  lesson_code?: any; // Required for Month view updates via addLessonToDate
};
type InstructorRoster = { id?: number; lessons?: LessonItem[] };
type TransportRoster = { id?: number; lessons?: LessonItem[] };

// Type for Month View State (matches DateItem in utils/common.ts)
type MonthViewLesson = {
  id: number | string;
  schedule_group_id?: number | string;
  [key: string]: any;
};
type MonthViewItem = {
  date: string;
  lessonCodes?: MonthViewLesson[];
};

// Socket event payload types
interface CheatSheetListPayload {
  date?: string;
  am?: string;
  fd?: string;
  pm?: string;
  skill_level?: string;
  lesson_code?: string;
  instructor_id_am?: number;
  instructor_id_fd?: number;
  instructor_id_pm?: number;
}

interface LunchListPayload {
  lunch_type?: string;
  lunch_date?: string;
  diet_id?: string | number;
  diet?: { id: number | string };
  kids_name?: string;
  quantity?: number;
}

interface SocketEventResponse {
  list?: LessonItem | CheatSheetListPayload | LunchListPayload;
  data?: {
    data?: LunchListPayload;
  };
}

// Type Definitions
interface SocketState {
  socket: Socket | null;
  token: string | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  resetSocketState: () => void;
  setToken: (token: string) => void;
  saveSocketId: (socketId: string) => void;
}

// Initialize token from persisted auth store
const storageData = localStorage.getItem('auth-store')
  ? JSON.parse(localStorage.getItem('auth-store')!)
  : {};
const tokenFromStorage = storageData?.state?.userInfo?.token ?? '';

const initialState: SocketState = {
  socket: null,
  token: tokenFromStorage,
  isConnected: false,
  connect: () => {}, // Placeholder for connect function
  disconnect: () => {}, // Placeholder for disconnect function
  resetSocketState: () => {}, // Placeholder for disconnect function
  setToken: () => {}, // Placeholder for setToken function
  saveSocketId: () => {} // Placeholder for saveSocketId function
};

const socketStore = (
  set: (
    partial:
      | Partial<SocketState>
      | ((state: SocketState) => Partial<SocketState>)
  ) => void,
  get: () => SocketState
) => ({
  ...initialState,

  connect: () => {
    const { token, socket, isConnected } = get();

    // Check if we are already connected with the same token
    const isTokenSame =
      (socket as any)?.io?.opts?.query?.token === (token || '');
    const isActuallyConnected = socket && socket.connected;

    if (socket && isConnected && isTokenSame && isActuallyConnected) {
      return;
    }

    // If token is the same but socket is disconnected, try to just reconnect first
    if (socket && isTokenSame && !isActuallyConnected) {
      socket.connect();
      return;
    }

    // Clean up existing connection if it exists (token changed or no socket)
    if (socket) {
      if ((socket as any)._cleanup) {
        (socket as any)._cleanup();
      }
      socket.off();
      socket.disconnect();
    }

    const newSocket = createSocketClient({
      url: SOCKET_URL,
      token,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000
    });

    set({ socket: newSocket, isConnected: false });

    newSocket.on('connect', () => {
      const socketId = newSocket.id;
      if (socketId && token) {
        set({ isConnected: true });
        get().saveSocketId(socketId);

        // Sync on Reconnect: Invalidate and refetch critical rosters
        // This ensures no data is lost during inactivity or disconnection
        queryClient.invalidateQueries({ queryKey: ['transport-roster'] });
        queryClient.invalidateQueries({ queryKey: ['lesson-schedule'] });
        queryClient.invalidateQueries({ queryKey: ['lesson-entries'] });
        queryClient.invalidateQueries({ queryKey: ['cheat-sheet-list'] });
        queryClient.invalidateQueries({ queryKey: ['cheat-sheet-lunch-list'] });
        queryClient.invalidateQueries({
          queryKey: ['instructor-roster-list']
        });
      }
    });

    newSocket.on('disconnect', (reason: string) => {
      set({ isConnected: false });
      // If disconnected by server, try to reconnect
      if (reason === 'io server disconnect') {
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (error: Error) => {
      console.error('Socket connection error:', error.message);
      set({ isConnected: false });
    });

    // Handle tab visibility and Safari/iOS specific events to ensure connection is alive
    const checkConnection = () => {
      const { socket, isConnected, token } = get();
      if (token && socket && !isConnected) {
        socket.connect();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConnection();
      }
    };

    // focus and pageshow are crucial for Safari/iOS stability
    window.addEventListener('focus', checkConnection);
    window.addEventListener('pageshow', checkConnection);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Proactive Watchdog: Check connection and handle Safari/iOS sleep suspends
    let lastTick = Date.now();
    const watchdogInterval = setInterval(() => {
      const now = Date.now();
      const timePassed = now - lastTick;
      lastTick = now;

      if (document.visibilityState === 'visible') {
        const { token, socket } = get();

        // If more than 45s passed between 30s ticks, the device likely slept
        const deviceWasAsleep = timePassed > 45000;

        if (token) {
          if (deviceWasAsleep && socket) {
            // Force a disconnect/reconnect if we were likely suspended
            socket.disconnect().connect();
          } else {
            // Regular check
            get().connect();
          }
        }
      }
    }, 30000);

    // Keep track of cleanup in the socket object
    (newSocket as any)._cleanup = () => {
      window.removeEventListener('focus', checkConnection);
      window.removeEventListener('pageshow', checkConnection);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(watchdogInterval);
    };

    newSocket.on('transportData', (res: SocketEventResponse) => {
      const list = res?.list as LessonItem | undefined;
      if (!list) return;

      const { selectedDate, transportRosterList, setTransportRosterList } =
        useTransportRosterStore.getState();
      const { id, start, car } = list;
      const updatedLessonSchedule = removeLessonById<
        TransportRoster,
        LessonItem
      >(transportRosterList as unknown as TransportRoster[], id as number);
      if (toYyyyMmDd(start) === selectedDate) {
        const updatedLessonScheduleNew = appendLessonToInstructor<
          TransportRoster,
          LessonItem
        >(updatedLessonSchedule, Number(car?.id ?? 0), list);
        setTransportRosterList(updatedLessonScheduleNew as TransportRoster[]);
      } else {
        setTransportRosterList(updatedLessonSchedule as TransportRoster[]);
      }
    });

    newSocket.on('deleteTransportData', (res: SocketEventResponse) => {
      const list = res?.list as LessonItem | undefined;
      if (!list) return;

      const { transportRosterList, setTransportRosterList } =
        useTransportRosterStore.getState();
      const { id } = list;
      const updatedLessonSchedule = removeLessonById<
        TransportRoster,
        LessonItem
      >(transportRosterList as unknown as TransportRoster[], id as number);
      setTransportRosterList(updatedLessonSchedule as TransportRoster[]);
    });

    newSocket.on('cheatSheet', (res: SocketEventResponse) => {
      const {
        cheatSheetDate,
        cheatSheetType,
        setCheatSheetData,
        cheatSheetData
      } = useCheatSheetStore.getState();
      const list = res?.list as CheatSheetListPayload | undefined;
      if (
        ['am', 'pm'].includes(cheatSheetType) &&
        list &&
        list.date === cheatSheetDate
      ) {
        const {
          am,
          fd,
          pm,
          skill_level,
          lesson_code,
          instructor_id_am,
          instructor_id_fd,
          instructor_id_pm
        } = list;
        if (!skill_level || !lesson_code) return;
        const code = skill_level.slice(0, 2);
        const currentCodeData =
          (cheatSheetData?.[code] as Record<string, unknown[]> | undefined) ??
          {};
        const currentLessonData = currentCodeData[lesson_code] ?? [];
        setCheatSheetData({
          ...(cheatSheetData || {}),
          [code]: {
            ...currentCodeData,
            [lesson_code]: currentLessonData.map((data: unknown) => {
              const dataRecord = data as Record<string, unknown>;
              const safeString = (v: number | string | undefined) =>
                v !== null && v !== undefined ? String(v) : null;
              const updatedData =
                dataRecord?.id === skill_level
                  ? {
                      am,
                      fd,
                      pm,
                      id: skill_level,
                      instructor_id_am: safeString(instructor_id_am),
                      instructor_id_fd: safeString(instructor_id_fd),
                      instructor_id_pm: safeString(instructor_id_pm)
                    }
                  : dataRecord;
              return updatedData;
            })
          }
        });
      }
    });

    newSocket.on('getLunch', (res: SocketEventResponse) => {
      const {
        cheatSheetDate,
        cheatSheetType,
        setCheatSheetData,
        cheatSheetData
      } = useCheatSheetStore.getState();
      const list = (res?.data?.data || res?.list) as
        | LunchListPayload
        | undefined;
      if (
        list &&
        list.lunch_type === cheatSheetType &&
        list.lunch_date === cheatSheetDate
      ) {
        const diet_id = list.diet?.id || list.diet_id;
        const { kids_name, quantity } = list;

        const updatedMain =
          cheatSheetData?.main?.map(
            (item: {
              id: string | number;
              quantity?: number;
              kids_name?: string;
              [key: string]: unknown;
            }) =>
              item.id == diet_id
                ? {
                    ...item,
                    quantity,
                    kids_name
                  }
                : item
          ) ?? [];

        const updatedSide =
          cheatSheetData?.side?.map(
            (item: {
              id: string | number;
              quantity?: number;
              kids_name?: string;
              [key: string]: unknown;
            }) =>
              item.id == diet_id
                ? {
                    ...item,
                    quantity,
                    kids_name
                  }
                : item
          ) ?? [];

        setCheatSheetData({
          ...cheatSheetData,
          main: updatedMain,
          side: updatedSide
        });
      }
    });

    newSocket.on('scheduleLessons', async (res: SocketEventResponse) => {
      const list = res?.list as LessonItem | undefined;
      if (!list) return;

      const currentRoute = window.location.pathname;
      const isInstructorCalendar = currentRoute.includes(
        ROUTE_PATHS.INSTRUCTOR_ROSTER_LIST
      );
      const { id, start, instructor, schedule_group } = list;
      // Daily Roster
      const { setLessonSchedule, selectedDate, selectedType } =
        useDailyRosterStore.getState();
      if (isInstructorCalendar) {
        // Instructor Roster
        const {
          selectedInstructor,
          selectedType: instructorSelectedType,
          selectedDate: instructorSelectedDate,
          setLessonSchedule: setInstructorLessonSchedule
        } = useInstructorRosterStore.getState();

        if (selectedInstructor && instructorSelectedDate) {
          const month = getMonth(new Date(instructorSelectedDate)) + 1;
          const newDataMonth = getMonth(new Date(start || '')) + 1;

          if (instructorSelectedType === 'Day') {
            setInstructorLessonSchedule(prev => {
              const current = (prev || []) as any[];
              const isMatch =
                month === newDataMonth &&
                String(selectedInstructor) === String(instructor?.id);
              const looksFlat = current.every(
                x => x && (x.start || x.string || String(x.id) === String(id))
              );

              if (looksFlat || current.length === 0) {
                const existingIndex = current.findIndex(
                  l => String(l.id) === String(id)
                );
                if (!isMatch)
                  return current.filter(l => String(l.id) !== String(id));
                if (existingIndex > -1) {
                  const next = [...current];
                  next[existingIndex] = list;
                  return next;
                }
                return [...current, list];
              } else {
                return current.map(inst => {
                  const lessons = (inst.lessons || []) as any[];
                  const isTarget = String(inst.id) === String(instructor?.id);
                  const existingIndex = lessons.findIndex(
                    l => String(l.id) === String(id)
                  );
                  if (isTarget) {
                    if (!isMatch)
                      return {
                        ...inst,
                        lessons: lessons.filter(
                          l => String(l.id) !== String(id)
                        )
                      };
                    if (existingIndex > -1) {
                      const nextL = [...lessons];
                      nextL[existingIndex] = list;
                      return { ...inst, lessons: nextL };
                    }
                    return { ...inst, lessons: [...lessons, list] };
                  }
                  return {
                    ...inst,
                    lessons: lessons.filter(l => String(l.id) !== String(id))
                  };
                }) as unknown as LessonSchedule[];
              }
            });
          }

          if (instructorSelectedType === 'Month') {
            setInstructorLessonSchedule(prev => {
              const current = (prev || []) as unknown as MonthViewItem[];
              const updated = removeLessonByScheduleGroupId(
                current,
                schedule_group?.id as number
              );
              if (String(selectedInstructor) === String(instructor?.id)) {
                return addLessonToDate(
                  updated,
                  list
                ) as unknown as LessonSchedule[];
              }
              return updated as unknown as LessonSchedule[];
            });
          }
        }
      } else {
        if (selectedDate && selectedType) {
          if (selectedType === 'Day') {
            setLessonSchedule(prev => {
              const updated = removeLessonById<InstructorRoster, LessonItem>(
                prev as unknown as InstructorRoster[],
                id as number
              );
              if (toYyyyMmDd(start) === selectedDate) {
                return appendLessonToInstructor<InstructorRoster, LessonItem>(
                  updated,
                  Number(instructor?.id ?? 0),
                  list
                ) as unknown as DailyRosterInstructor[];
              }
              return updated as unknown as DailyRosterInstructor[];
            });
          }

          if (selectedType === 'Month') {
            setLessonSchedule(prev => {
              const updated = removeLessonByScheduleGroupId(
                prev as unknown as MonthViewItem[],
                schedule_group?.id as number
              );
              return addLessonToDate(
                updated,
                list
              ) as unknown as DailyRosterInstructor[];
            });
          }
        }
      }
    });

    newSocket.on('deleteScheduleLessons', async (res: SocketEventResponse) => {
      const list = res?.list as LessonItem | undefined;
      if (!list) return;
      const { id, start, schedule_group } = list;

      // Daily Roster
      const {
        setLessonSchedule,
        selectedDate,
        selectedType,
        setDeletedRecord
      } = useDailyRosterStore.getState();
      setDeletedRecord(list as unknown as Record<string, unknown> | null);
      if (selectedDate && selectedType) {
        if (selectedType === 'Day') {
          setLessonSchedule(prev => {
            const updated = removeLessonById<InstructorRoster, LessonItem>(
              prev as unknown as InstructorRoster[],
              id as number
            );
            if (toYyyyMmDd(start) === selectedDate) {
              return updated as unknown as DailyRosterInstructor[];
            }
            return prev as unknown as DailyRosterInstructor[];
          });
        }

        if (selectedType === 'Month') {
          setLessonSchedule(prev => {
            return removeLessonByScheduleGroupId(
              prev as unknown as MonthViewItem[],
              schedule_group?.id as number
            ) as unknown as DailyRosterInstructor[];
          });
        }
      }

      // Instructor Roster
      const {
        selectedInstructor,
        selectedType: instructorSelectedType,
        selectedDate: instructorSelectedDate,
        setLessonSchedule: setInstructorLessonSchedule
      } = useInstructorRosterStore.getState();
      if (selectedInstructor && instructorSelectedDate) {
        const month = getMonth(new Date(instructorSelectedDate)) + 1;
        const newDataMonth = getMonth(new Date(start || '')) + 1;

        if (instructorSelectedType === 'Day') {
          if (month === newDataMonth) {
            setInstructorLessonSchedule(prev => {
              const current = (prev || []) as any[];
              const looksFlat = current.every(
                x => x && (x.start || x.string || String(x.id) === String(id))
              );
              if (looksFlat) {
                return current.filter(l => String(l.id) !== String(id));
              }
              return removeLessonById(
                current,
                id as number
              ) as unknown as LessonSchedule[];
            });
          }
        }

        if (instructorSelectedType === 'Month') {
          setInstructorLessonSchedule(prev => {
            const current = (prev || []) as unknown as MonthViewItem[];
            return removeLessonByScheduleGroupId(
              current,
              schedule_group?.id as number
            ) as unknown as LessonSchedule[];
          });
        }
      }
    });

    newSocket.on('onUndoDeletedRecord', async (res: SocketEventResponse) => {
      const list = res?.list as LessonItem | undefined;
      const { setDeletedRecord } = useDailyRosterStore.getState();
      setDeletedRecord(list as unknown as Record<string, unknown> | null);
    });
  },

  disconnect: () => {
    set((state: SocketState) => {
      if (state.socket) {
        if ((state.socket as any)._cleanup) {
          (state.socket as any)._cleanup();
        }
        state.socket.off();
        state.socket.disconnect();
      }
      return { socket: null, isConnected: false };
    });
  },

  setToken: (token: string) => {
    set({ token });
  },

  saveSocketId: (socketId: string) => {
    set((state: SocketState) => {
      const { socket, token } = state;
      if (socket && token && state.isConnected) {
        socket.emit('saveSocketId', {
          socketId,
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      return state;
    });
  },

  // Reset state function
  resetSocketState: () => {
    set((state: SocketState) => {
      if (state.socket) {
        if ((state.socket as any)._cleanup) {
          (state.socket as any)._cleanup();
        }
        state.socket.off();
        state.socket.disconnect();
      }
      return { ...initialState };
    });
  }
});

const useSocketStore = create<SocketState>()(devtools(socketStore));

export const {
  connect,
  disconnect,
  setToken,
  resetSocketState // Export the reset function
} = useSocketStore.getState();

export default useSocketStore;
